import React from 'react'
import { motion } from 'framer-motion'
import { Info, CheckCircle2, Lightbulb, ArrowRight, HelpCircle, Shield } from 'lucide-react'

const REGISTRATION_STEPS = [
    {
        id: 1,
        name: 'Legal Entity Details',
        description: 'Company information and registration details'
    },
    {
        id: 2,
        name: 'Profile Building',
        description: 'Build your company profile and showcase expertise'
    },
    {
        id: 3,
        name: 'Subscription Plan',
        description: 'Choose a plan that fits your needs'
    },
    {
        id: 4,
        name: 'Document Upload',
        description: 'Upload required documents for verification'
    }
]

const STEP_TIPS = {
    1: [
        'CIN/LLP-IN is a 21-character alphanumeric code issued by the Ministry of Corporate Affairs',
        'PAN must be exactly 10 characters (5 letters, 4 numbers, 1 letter)',
        'GSTIN is optional but recommended for tax compliance',
        'Registered and operating addresses can be the same if your business operates from the registered location',
        'Contact person should be an authorized representative of the company'
    ],
    2: [
        'Provide a clear and concise company description that highlights your expertise',
        'Select industry domains that best represent your business focus',
        'List key technologies and skills your team specializes in',
        'Upload a professional logo and brand assets to enhance your profile',
        'Include social media links to showcase your online presence'
    ],
    3: [
        'Free plan is perfect for getting started with basic features',
        'Basic plan offers more resources and project matching capabilities',
        'Premium plan provides advanced features and priority support',
        'You can upgrade or downgrade your plan at any time',
        'All plans include email support and access to the vendor dashboard'
    ],
    4: [
        'Upload clear, high-quality scans or photos of all required documents',
        'Ensure documents are valid and not expired',
        'Documents will be reviewed by our compliance team',
        'You\'ll receive notifications about the verification status',
        'All documents are securely stored and encrypted'
    ]
}

const STEP_NEXT_INFO = {
    1: 'After completing this step, you\'ll proceed to build your company profile, select a subscription plan, and upload required documents for verification.',
    2: 'After completing your profile, you\'ll select a subscription plan and then upload required documents for verification.',
    3: 'After selecting your plan, you\'ll proceed to upload required documents for verification and review.',
    4: 'After uploading documents, our team will review them. You\'ll be notified once verification is complete and can access your dashboard.'
}

const RegistrationSidebar = ({ currentStep = 1, isStepCompleted = false }) => {
    const tips = STEP_TIPS[currentStep] || []
    const nextInfo = STEP_NEXT_INFO[currentStep] || ''

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="hidden lg:block w-80 flex-shrink-0"
        >
            <div className="bg-gradient-to-br from-white to-primary-50/30 dark:from-secondary-800 dark:to-secondary-900 rounded-xl p-6 shadow-lg border border-primary-100 dark:border-secondary-700 sticky top-24">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-primary-200 dark:border-primary-800">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-md">
                        <Info className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-secondary-900 dark:text-secondary-100">
                            Helpful Information
                        </h3>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">Registration guide</p>
                    </div>
                </div>

                <div className="space-y-5">
                    {/* Step Progress */}
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <div className="w-1 h-4 bg-gradient-to-b from-primary-500 to-primary-600 rounded-full"></div>
                            <p className="text-xs font-bold text-secondary-800 dark:text-secondary-200 uppercase tracking-wide">
                                Registration Steps
                            </p>
                        </div>
                        <div className="space-y-2.5 pl-3">
                            {REGISTRATION_STEPS.map((step) => (
                                <div key={step.id} className="flex items-start gap-3 group">
                                    <div
                                        className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold transition-all duration-300 shadow-sm ${step.id === currentStep
                                            ? 'bg-gradient-to-br from-primary-500 to-primary-600 text-white scale-110 ring-2 ring-primary-300 dark:ring-primary-700'
                                            : step.id < currentStep
                                                ? 'bg-gradient-to-br from-success-500 to-success-600 text-white'
                                                : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-400 dark:text-secondary-500 group-hover:bg-secondary-200 dark:group-hover:bg-secondary-600'
                                            }`}
                                    >
                                        {step.id < currentStep ? <CheckCircle2 className="w-4 h-4" /> : step.id}
                                    </div>
                                    <div className="flex-1 pt-0.5">
                                        <span
                                            className={`text-xs font-medium block ${step.id === currentStep
                                                ? 'text-primary-700 dark:text-primary-300 font-semibold'
                                                : step.id < currentStep
                                                    ? 'text-success-700 dark:text-success-300'
                                                    : 'text-secondary-600 dark:text-secondary-400'
                                                }`}
                                        >
                                            {step.name}
                                        </span>
                                        {step.id === currentStep && (
                                            <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1 leading-relaxed">
                                                {step.description}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Tips Section */}
                    {tips.length > 0 && (
                        <div className="space-y-3 pt-4 border-t-2 border-primary-100 dark:border-secondary-700">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center">
                                    <Lightbulb className="w-4 h-4 text-accent-600 dark:text-accent-400" />
                                </div>
                                <p className="text-xs font-bold text-secondary-800 dark:text-secondary-200 uppercase tracking-wide">
                                    Quick Tips
                                </p>
                            </div>
                            <ul className="space-y-2.5 pl-2">
                                {tips.map((tip, index) => (
                                    <li key={index} className="flex items-start gap-2.5 group">
                                        <div className="w-5 h-5 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mt-0.5 flex-shrink-0 group-hover:bg-primary-200 dark:group-hover:bg-primary-900/50 transition-colors">
                                            <CheckCircle2 className="w-3 h-3 text-primary-600 dark:text-primary-400" />
                                        </div>
                                        <span className="text-xs text-secondary-700 dark:text-secondary-300 leading-relaxed">{tip}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* What's Next Info */}
                    {nextInfo && (
                        <div className="space-y-2.5 pt-4 border-t-2 border-primary-100 dark:border-secondary-700 bg-primary-50/50 dark:bg-primary-900/10 rounded-lg p-3">
                            <div className="flex items-center gap-2">
                                <ArrowRight className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                                <p className="text-xs font-bold text-primary-800 dark:text-primary-200 uppercase tracking-wide">
                                    What's Next?
                                </p>
                            </div>
                            <p className="text-xs text-secondary-700 dark:text-secondary-300 leading-relaxed pl-6">
                                {nextInfo}
                            </p>
                        </div>
                    )}

                    {/* Edit Restriction Notice - Show when step is completed */}
                    {/* Edit Restriction Notice - Show when step is completed */}
                    {isStepCompleted ? (
                        <div className="pt-4 border-t-2 border-warning-200 dark:border-warning-800">
                            <div className="flex items-start gap-2.5 bg-warning-50 dark:bg-warning-900/20 rounded-lg p-3 border border-warning-200 dark:border-warning-800">
                                <Shield className="w-4 h-4 text-warning-600 dark:text-warning-400 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                    <p className="text-xs font-semibold text-warning-800 dark:text-warning-300 mb-1.5">
                                        Edit Restriction
                                    </p>
                                    <p className="text-xs text-warning-700 dark:text-warning-400 leading-relaxed">
                                        This step has been submitted. Admin approval is required to edit details.
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="pt-4 border-t-2 border-blue-100 dark:border-blue-900/30">
                            <div className="flex items-start gap-2.5 bg-blue-50 dark:bg-blue-900/10 rounded-lg p-3 border border-blue-100 dark:border-blue-800">
                                <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                    <p className="text-xs font-semibold text-blue-800 dark:text-blue-300 mb-1.5">
                                        Submission Notice
                                    </p>
                                    <p className="text-xs text-blue-700 dark:text-blue-400 leading-relaxed">
                                        Submit carefully. You cannot edit details once submitted; admin approval will be needed for changes.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Support Info */}
                    <div className="pt-4 border-t-2 border-primary-100 dark:border-secondary-700">
                        <div className="flex items-start gap-2.5 bg-secondary-50 dark:bg-secondary-800/50 rounded-lg p-3">
                            <HelpCircle className="w-4 h-4 text-secondary-500 dark:text-secondary-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-secondary-600 dark:text-secondary-400 leading-relaxed">
                                <span className="font-semibold">Need help?</span> Contact our support team for assistance with registration.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    )
}

export default RegistrationSidebar
